import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

// Compact radio-group control for finite-option props
// (services.columns, pricing.columns, gallery.layout, heading.align,
// text.align, hero.layout, etc.). Single primitive, wide reuse across
// the inspector forms.

export interface SegmentedOption<T extends string | number> {
  value: T;
  // A node, not just a string: routes/locations.tsx marks the locations with
  // no map data yet ("Barrio Logan — no data") inside the label, and losing
  // that would have meant losing the signal, not simplifying it. Every other
  // call site passes a plain string, which is already a ReactNode.
  label: ReactNode;
}

interface SegmentedControlProps<T extends string | number> {
  ariaLabel: string;
  value: T;
  options: ReadonlyArray<SegmentedOption<T>>;
  onChange: (next: T) => void;
  // Disables the whole group. Added for the screens that adopted this
  // primitive in the design audit (reputation/reviews.tsx disables the
  // channel switch while an ask is in flight) — the inspector callers never
  // pass it, so the default stays "always enabled".
  disabled?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string | number>({
  ariaLabel,
  value,
  options,
  onChange,
  disabled = false,
  className,
}: SegmentedControlProps<T>) {
  // Adversarial round A1/B3: this claims `role="radiogroup"` and every option
  // was tabbable, which is the wrong contract — a radio group is ONE tab stop
  // and moves between options with the arrow keys. Roving tabindex + the
  // handler below make the promise true; the refs exist only so selecting
  // with a key also MOVES focus (a radio group's focus follows selection).
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const checkedIndex = options.findIndex((opt) => opt.value === value);

  const select = (index: number) => {
    const opt = options[index];
    if (!opt) return;
    refs.current[index]?.focus();
    if (opt.value !== value) onChange(opt.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (disabled || options.length === 0) return;
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + options.length) % options.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    select(next);
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        // `flex-wrap` is opt-in via className: the option lists that come
        // from customer data (search terms, location names) can be long
        // enough to run off a narrow screen, while the fixed 2–4 option
        // groups never wrap and read better on one line.
        'inline-flex p-0.5 gap-0.5',
        // `self-start`: `inline-flex` still stretches to full width inside a
        // flex-col parent, which made the strip read as a full-width bar.
        'self-start',
        'rounded-[var(--radius-sm)]',
        'bg-surface-sunken border border-border-subtle',
        className,
      )}
    >
      {options.map((opt, index) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            // One tab stop for the whole group. When nothing is checked
            // (a value outside the option list) the first option takes it,
            // so the group is never unreachable by keyboard.
            tabIndex={active || (checkedIndex === -1 && index === 0) ? 0 : -1}
            onKeyDown={(event) => onKeyDown(event, index)}
            onClick={() => {
              if (!active) onChange(opt.value);
            }}
            className={cn(
              // T11/A7: h-8 (32 px) is fine with a mouse but under the
              // 44 pt touch minimum, and this primitive now backs three
              // CUSTOMER screens (reviews channel, billing interval, AI
              // engine), not just the inspector. min-h on a coarse
              // pointer only — the desktop control keeps its compact 32 px.
              'h-8 min-w-[2.5rem] px-3 pointer-coarse:min-h-11',
              'text-sm font-medium',
              'rounded-[calc(var(--radius-sm)-2px)]',
              'transition-colors duration-[var(--duration-fast)]',
              'focus-visible:outline-2 focus-visible:outline-brand-500',
              'focus-visible:outline-offset-1',
              'disabled:pointer-events-none disabled:opacity-50',
              active
                ? // B3: card-on-sunken alone was too faint a difference to
                  // read as "selected" in the screenshots; the hairline ring
                  // gives the selected option a visible edge.
                  'bg-card text-foreground shadow-sm ring-1 ring-border-strong'
                : 'text-text-secondary hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
